import { useState, useEffect, useRef } from 'react';
import { EmotionType } from '../types/emotion';
import { AVAILABLE_EMOTIONS, getEmotionImagePath } from '../config/emotions';

interface AvatarState {
  currentEmotion: EmotionType;
  nextEmotion: EmotionType | null;
  isTransitioning: boolean;
}

interface AvatarDisplayProps {
  onEmotionChange?: (emotion: EmotionType) => void;
  className?: string;
  /** Custom avatar images from Firebase Storage */
  avatarImages?: Record<EmotionType, string>;
  /** Controlled emotion (for integration with WebSocket/AI) */
  controlledEmotion?: EmotionType;
  /** Disable auto-cycling when controlled */
  disableAutoCycle?: boolean;
  /** Desktop foreground image mode: 'cover' fills screen (default), 'contain' shows full image with blurred bars */
  imageMode?: 'cover' | 'contain';
}

// Default avatar emotion images (fallback) - dynamically generated from AVAILABLE_EMOTIONS
const DEFAULT_EMOTION_IMAGES: Record<EmotionType, string> = Object.fromEntries(
  AVAILABLE_EMOTIONS.map(emotion => [emotion, getEmotionImagePath(emotion)])
) as Record<EmotionType, string>;

export default function AvatarDisplay({ 
  onEmotionChange, 
  className = '',
  avatarImages,
  controlledEmotion,
  disableAutoCycle = false,
  imageMode = 'cover'
}: AvatarDisplayProps) {
  // Use custom images if provided, otherwise use default images
  const EMOTION_IMAGES = avatarImages || DEFAULT_EMOTION_IMAGES;
  const [avatarState, setAvatarState] = useState<AvatarState>({
    currentEmotion: 'neutral',
    nextEmotion: null,
    isTransitioning: false
  });

  // NOTE: No loading state needed here!
  // Images are already preloaded by useSessionLifecycle before this component mounts.
  // The preloading happens during the "Preparing scenario" loading screen.

  const changeEmotionRef = useRef<((emotion: EmotionType) => void) | null>(null);

  const changeEmotion = (newEmotion: EmotionType) => {
    if (newEmotion === avatarState.currentEmotion || avatarState.isTransitioning) return;

    const imageUrl = EMOTION_IMAGES[newEmotion];

    // If no image exists for this emotion, accept the emotion change
    // (to prevent infinite loops) but skip the visual transition.
    if (!imageUrl) {
      setAvatarState({
        currentEmotion: newEmotion,
        nextEmotion: null,
        isTransitioning: false
      });
      onEmotionChange?.(newEmotion);
      return;
    }

    // Pre-ensure image is loaded before starting transition
    const img = new Image();
    img.onload = () => {
      // Image is ready! Now we can safely do the crossfade
      
      // Step 1: Set up next emotion (overlay starts at opacity 0)
      setAvatarState(prev => ({ 
        ...prev, 
        nextEmotion: newEmotion,
        isTransitioning: false  // Start with transition false
      }));

      // Step 2: Small delay, then trigger transition (opacity 0 → 1)
      setTimeout(() => {
        setAvatarState(prev => ({ 
          ...prev, 
          isTransitioning: true 
        }));
      }, 50); // Small delay to ensure overlay is rendered at opacity 0

      // Step 3: After cross-fade completes, make it the new current
      setTimeout(() => {
        setAvatarState({
          currentEmotion: newEmotion,
          nextEmotion: null,
          isTransitioning: false
        });
        
        onEmotionChange?.(newEmotion);
      }, 600); // 50ms setup + 500ms transition + 50ms buffer
    };

    // If the image fails to load, still accept the emotion change to prevent loops
    img.onerror = () => {
      console.warn(`[AvatarDisplay] Failed to load image for emotion: ${newEmotion}`);
      setAvatarState({
        currentEmotion: newEmotion,
        nextEmotion: null,
        isTransitioning: false
      });
      onEmotionChange?.(newEmotion);
    };
    
    // If image is already cached (complete), onload fires immediately
    img.src = imageUrl;
  };

  // Update ref whenever changeEmotion changes
  useEffect(() => {
    changeEmotionRef.current = changeEmotion;
  }, [changeEmotion]);

  // Handle controlled emotion changes from parent
  useEffect(() => {
    if (controlledEmotion && controlledEmotion !== avatarState.currentEmotion && !avatarState.isTransitioning) {
      changeEmotion(controlledEmotion);
    }
  }, [controlledEmotion, avatarState.currentEmotion, avatarState.isTransitioning, changeEmotion]);

  // Auto-cycle through emotions for demo (only when not controlled)
  useEffect(() => {
    if (disableAutoCycle || controlledEmotion) return;

    const emotions = Object.keys(EMOTION_IMAGES) as EmotionType[];
    let currentIndex = 0;

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % emotions.length;
      if (changeEmotionRef.current) {
        changeEmotionRef.current(emotions[currentIndex]);
      }
    }, 3000); // More natural cycling pace

    return () => clearInterval(interval);
  }, [disableAutoCycle, controlledEmotion]);

  return (
    <div className={`avatar-display ${className}`}>
      {/* Avatar Display - Full viewport (width + height) */}
      <div className="relative w-full h-screen bg-black overflow-hidden shadow-lg">
        <div
          className="relative w-full h-full select-none"
          onContextMenu={(e) => e.preventDefault()}
          style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
        >
          {/* Base image - always visible, using <img> tag for proper preloading */}
          {/* Falls back to neutral image when current emotion has no image */}
          
          {/* Mobile version - shows more body/scene (objectPosition 25%) */}
          <img 
            src={EMOTION_IMAGES[avatarState.currentEmotion] || EMOTION_IMAGES['neutral']}
            alt={`Avatar emotion: ${avatarState.currentEmotion}`}
            className="absolute inset-0 w-full h-full object-cover md:hidden pointer-events-none"
            style={{ objectPosition: 'center 25%' }}
            draggable={false}
          />
          
          {/* Desktop version - Cinematic blurred background + contained foreground */}
          <div className="absolute inset-0 hidden md:block overflow-hidden bg-black pointer-events-none">
            {/* Blurred background - using inline filter for perfect cinematic blur with high scaling */}
            <div className="absolute top-1/2 left-1/2 w-full h-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ filter: 'blur(50px) brightness(0.5) saturate(1.5)', transform: 'translate(-50%, -50%) scale(1.5)' }}>
              <img 
                src={EMOTION_IMAGES[avatarState.currentEmotion] || EMOTION_IMAGES['neutral']}
                alt=""
                className="w-full h-full object-cover pointer-events-none opacity-80"
                style={{ objectPosition: 'center center' }}
                draggable={false}
              />
            </div>
            {/* Dark overlay to make avatar pop more */}
            <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
            
            {/* Foreground image - mode depends on image aspect ratio */}
            <img 
              src={EMOTION_IMAGES[avatarState.currentEmotion] || EMOTION_IMAGES['neutral']}
              alt={`Avatar emotion: ${avatarState.currentEmotion}`}
              className={`absolute inset-0 w-full h-full ${imageMode === 'contain' ? 'object-contain' : 'object-cover'} pointer-events-none drop-shadow-2xl`}
              style={{ objectPosition: 'center center' }}
              draggable={false}
            />
          </div>
          
          {/* Overlay image for cross-fade - starts at opacity 0, fades to 1 */}
          {avatarState.nextEmotion && EMOTION_IMAGES[avatarState.nextEmotion] && (
            <>
              {/* Mobile overlay */}
              <img 
                src={EMOTION_IMAGES[avatarState.nextEmotion]}
                alt={`Avatar transitioning to: ${avatarState.nextEmotion}`}
                className="absolute inset-0 w-full h-full object-cover md:hidden pointer-events-none"
                style={{
                  objectPosition: 'center 25%',
                  opacity: avatarState.isTransitioning ? 1 : 0,
                  transition: 'opacity 500ms ease-out'
                }}
                draggable={false}
              />
              
              {/* Desktop overlay */}
              <div 
                className="absolute inset-0 hidden md:block overflow-hidden pointer-events-none"
                style={{
                  opacity: avatarState.isTransitioning ? 1 : 0,
                  transition: 'opacity 500ms ease-out'
                }}
              >
                {/* Blurred background overlay */}
                <div className="absolute top-1/2 left-1/2 w-full h-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ filter: 'blur(50px) brightness(0.5) saturate(1.5)', transform: 'translate(-50%, -50%) scale(1.5)' }}>
                  <img 
                    src={EMOTION_IMAGES[avatarState.nextEmotion]}
                    alt=""
                    className="w-full h-full object-cover pointer-events-none opacity-80"
                    style={{ objectPosition: 'center center' }}
                    draggable={false}
                  />
                </div>
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
                
                {/* Foreground image overlay - mode depends on image aspect ratio */}
                <img 
                  src={EMOTION_IMAGES[avatarState.nextEmotion]}
                  alt={`Avatar transitioning to: ${avatarState.nextEmotion}`}
                  className={`absolute inset-0 w-full h-full ${imageMode === 'contain' ? 'object-contain' : 'object-cover'} pointer-events-none drop-shadow-2xl`}
                  style={{ objectPosition: 'center center' }}
                  draggable={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
